import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
  PLATFORM_ID,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';
import { GeoCoordinates } from 'src/app/interfaces/geo-coordinates.interface';
import {
  GoogleGeocoder,
  GoogleMap,
  GoogleMapsApi,
  GoogleMapsEventListener,
  GoogleMapsMouseEvent,
  GoogleMarker,
  GooglePlaceResult,
} from 'src/app/models/google-maps.model';
import { GoogleMapsLoaderService } from 'src/app/services/google-maps-loader.service';

@Component({
  selector: 'app-location-map-picker',
  templateUrl: './location-map-picker.component.html',
  styleUrl: './location-map-picker.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [MatFormField, MatInput, MatIcon, MatProgressSpinner],
})
export class LocationMapPickerComponent implements AfterViewInit, OnDestroy {
  readonly coordinates = input<GeoCoordinates | null>(null);
  readonly coordinates_change = output<GeoCoordinates>();
  readonly location_change = output<LocationMapSelection>();
  readonly load_error_change = output<string | null>();
  readonly mode = input<LocationDialogModeEnum>(LocationDialogModeEnum.Create);
  readonly LocationDialogModeEnum = LocationDialogModeEnum;

  @ViewChild('map_element', { read: ElementRef }) private map_element: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('search_input', { read: ElementRef }) private search_input: ElementRef<HTMLInputElement> | undefined;

  private readonly maps_loader = inject(GoogleMapsLoaderService);
  private readonly ng_zone = inject(NgZone);
  private readonly platform_id = inject(PLATFORM_ID);

  readonly loading = signal(true);
  readonly load_error = signal<string | null>(null);
  readonly selected_coordinates = signal<GeoCoordinates | null>(null);

  private google_maps: GoogleMapsApi | null = null;
  private geocoder: GoogleGeocoder | null = null;
  private map: GoogleMap | null = null;
  private marker: GoogleMarker | null = null;
  private map_click_listener: GoogleMapsEventListener | null = null;
  private marker_drag_listener: GoogleMapsEventListener | null = null;
  private place_changed_listener: GoogleMapsEventListener | null = null;
  private reverse_geocode_request_id = 0;
  private destroyed = false;

  ngAfterViewInit(): void {
    void this.initializeGoogleMaps();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.map_click_listener?.remove();
    this.marker_drag_listener?.remove();
    this.place_changed_listener?.remove();
    this.marker?.setMap(null);
  }

  private async initializeGoogleMaps(): Promise<void> {
    if (!isPlatformBrowser(this.platform_id)) {
      this.loading.set(false);
      this.setLoadError('Google Maps no está disponible.');
      return;
    }

    try {
      this.google_maps = await this.maps_loader.load();

      if (this.destroyed) {
        return;
      }

      const { map_element, search_input } = await this.waitForViewElements();

      if (this.destroyed) {
        return;
      }

      this.initializeMap(map_element);
      this.initializeAutocomplete(search_input);
      this.setLoadError(null);
    } catch (error: unknown) {
      this.setLoadError(error instanceof Error ? error.message : 'No se pudo cargar Google Maps.');
    } finally {
      this.loading.set(false);
    }
  }

  private initializeMap(map_element: HTMLDivElement): void {
    if (this.google_maps == null) {
      return;
    }

    const selected_coordinates = this.coordinates();
    const initial_coordinates = selected_coordinates ?? DEFAULT_COORDINATES;
    const mode = this.mode();
    const position = this.toGoogleCoordinates(initial_coordinates);
    this.selected_coordinates.set(selected_coordinates);

    this.geocoder = new this.google_maps.maps.Geocoder();
    this.map = new this.google_maps.maps.Map(map_element, {
      center: position,
      zoom: selected_coordinates == null ? DEFAULT_ZOOM : SELECTED_ZOOM,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
    });

    this.marker = new this.google_maps.maps.Marker({
      draggable: mode !== LocationDialogModeEnum.View,
      clickable: mode !== LocationDialogModeEnum.View,
      map: this.map,
      position,
      visible: selected_coordinates != null,
    });

    if (mode !== LocationDialogModeEnum.View) {
      this.map_click_listener = this.map.addListener('click', (event: GoogleMapsMouseEvent): void => {
        this.ng_zone.run((): void => {
          this.selectCoordinatesFromMapEvent(event);
        });
      });

      this.marker_drag_listener = this.marker.addListener('dragend', (event: GoogleMapsMouseEvent): void => {
        this.ng_zone.run((): void => {
          this.selectCoordinatesFromMapEvent(event);
        });
      });
    }

    this.syncMapFromCoordinates(false);
  }

  private initializeAutocomplete(search_input: HTMLInputElement): void {
    if (this.google_maps == null) {
      return;
    }

    const autocomplete = new this.google_maps.maps.places.Autocomplete(search_input, {
      fields: ['formatted_address', 'geometry', 'name'],
    });

    this.place_changed_listener = autocomplete.addListener('place_changed', (): void => {
      this.ng_zone.run((): void => {
        const place = autocomplete.getPlace();
        const location = place.geometry?.location;

        if (location == null) {
          return;
        }

        this.reverse_geocode_request_id++;
        this.selectLocation(
          {
            lat: location.lat(),
            lon: location.lng(),
            name: this.resolvePlaceName(place),
          },
          true
        );
      });
    });
  }

  private selectCoordinatesFromMapEvent(event: GoogleMapsMouseEvent): void {
    const lat_lng = event.latLng;

    if (lat_lng == null) {
      return;
    }

    const location: LocationMapSelection = {
      lat: lat_lng.lat(),
      lon: lat_lng.lng(),
    };
    const reverse_geocode_request_id = ++this.reverse_geocode_request_id;

    this.selectLocation(location, true);
    void this.emitReverseGeocodedLocation(location, reverse_geocode_request_id);
  }

  private selectLocation(location: LocationMapSelection, emit_change: boolean, center_map = true): void {
    this.selected_coordinates.set({
      lat: location.lat,
      lon: location.lon,
    });
    this.syncMapFromCoordinates(center_map);

    if (emit_change) {
      this.coordinates_change.emit({
        lat: location.lat,
        lon: location.lon,
      });
      this.location_change.emit(location);
    }
  }

  private syncMapFromCoordinates(center_map: boolean): void {
    if (this.map == null || this.marker == null) {
      return;
    }

    const coordinates = this.selected_coordinates();

    if (coordinates == null) {
      this.marker.setVisible(false);
      return;
    }

    const position = this.toGoogleCoordinates(coordinates);
    this.marker.setPosition(position);
    this.marker.setVisible(true);

    if (center_map) {
      this.map.setCenter(position);
      this.map.setZoom(SELECTED_ZOOM);
    }
  }

  private toGoogleCoordinates(coordinates: GeoCoordinates): { lat: number; lng: number } {
    return {
      lat: coordinates.lat,
      lng: coordinates.lon,
    };
  }

  private resolvePlaceName(place: GooglePlaceResult): string | undefined {
    const name = place.name?.trim();

    if (name != null && name !== '') {
      return name;
    }

    const formatted_address = place.formatted_address?.trim();

    return formatted_address === '' ? undefined : formatted_address;
  }

  private async emitReverseGeocodedLocation(location: LocationMapSelection, request_id: number): Promise<void> {
    const name = await this.reverseGeocode(location);

    if (this.destroyed || request_id !== this.reverse_geocode_request_id || name == null) {
      return;
    }

    this.ng_zone.run((): void => {
      this.selectLocation(
        {
          ...location,
          name,
        },
        true,
        false
      );
    });
  }

  private reverseGeocode(coordinates: GeoCoordinates): Promise<string | null> {
    if (this.geocoder == null) {
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      this.geocoder?.geocode({ location: this.toGoogleCoordinates(coordinates) }, (results, status): void => {
        if (status !== 'OK' || results == null || results.length === 0) {
          resolve(null);
          return;
        }

        resolve(results[0]?.formatted_address?.trim() || null);
      });
    });
  }

  private async waitForViewElements(): Promise<{ map_element: HTMLDivElement; search_input: HTMLInputElement }> {
    for (let attempt = 0; attempt < 10; attempt++) {
      const map_element = this.map_element?.nativeElement;
      const search_input = this.search_input?.nativeElement;

      const has_elements =
        map_element instanceof HTMLDivElement &&
        map_element.isConnected &&
        search_input instanceof HTMLInputElement &&
        search_input.isConnected;

      if (has_elements) {
        return { map_element, search_input };
      }

      await this.nextAnimationFrame();
    }

    throw new Error('No se pudo inicializar el contenedor de Google Maps.');
  }

  private nextAnimationFrame(): Promise<void> {
    return new Promise(resolve => {
      requestAnimationFrame((): void => resolve());
    });
  }

  private setLoadError(load_error: string | null): void {
    this.load_error.set(load_error);
    this.load_error_change.emit(load_error);
  }
}

export interface LocationMapSelection extends GeoCoordinates {
  name?: string;
}

const DEFAULT_COORDINATES: GeoCoordinates = {
  lat: 41.9794,
  lon: 2.8214,
};

const DEFAULT_ZOOM = 8;
const SELECTED_ZOOM = 10;
